import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { getUserRoles, isLoggedIn, subscribeToAuthChanges } from '@services/auth';
import { getCurrentSubdomain } from '@utils/subdomain';
import { BrandText } from '@components/BrandText';
import { openConsentPreferences } from '@components/ConsentManager';
import './Footer.css';

const SUPER_ADMIN_ROLE = 'SuperAdmin';

function Footer() {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(isLoggedIn());
  const [isSuperAdmin, setIsSuperAdmin] = useState(() => {
    if (!isLoggedIn()) return false;
    const roles = getUserRoles();
    return roles.some((r) => r.toLowerCase() === SUPER_ADMIN_ROLE.toLowerCase());
  });
  const hasSubdomain = Boolean(getCurrentSubdomain());

  useEffect(() => {
    const handleAuthChange = () => {
      const loggedIn = isLoggedIn();
      setIsAuthenticated(loggedIn);

      if (!loggedIn) {
        setIsSuperAdmin(false);
        return;
      }

      const roles = getUserRoles();
      setIsSuperAdmin(roles.some((r) => r.toLowerCase() === SUPER_ADMIN_ROLE.toLowerCase()));
    };

    const unsubscribe = subscribeToAuthChanges(handleAuthChange);
    return unsubscribe;
  }, []);

  return (
    <footer className="footer">
      <div className="footer-links">
        <Link to="/legal-notice">{t('nav.legalNotice', 'Legal Notice')}</Link>
        <Link to="/terms">{t('nav.terms', 'Terms of Service')}</Link>
        <Link to="/privacy">{t('nav.privacy', 'Privacy')}</Link>
        <Link to="/cookies">{t('nav.cookies', 'Cookies and storage')}</Link>
        <button type="button" className="footer-link" onClick={openConsentPreferences}>
          {t('nav.privacyChoices', 'Privacy choices')}
        </button>
        <Link to="/faq">{t('nav.faq', 'FAQ')}</Link>
        {isSuperAdmin && <Link to="/__stats">{t('nav.stats', { defaultValue: 'Stats' })}</Link>}
        {!isAuthenticated && !hasSubdomain && (
          <Link to="/usage" className="footer-link anonymous-usage-footer-link">
            {t('upload.usageLink', { defaultValue: 'Usage' })}
          </Link>
        )}
        <Link to="/contact">{t('nav.contact', 'Contact')}</Link>
      </div>
      <div className="footer-copy">
        <BrandText
          text="©2026 — SendAllFiles. Built in Switzerland, sent everywhere."
        />
      </div>
    </footer>
  );
}

export default Footer;
